import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Panasonic } from '@thesvg/react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse, user, isMobileOpen, onMobileClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // UPDATED: Corrected role names to match database ('system_admin')
  const isPic = user?.role === 'pic' || user?.role === 'department_pic';

  const navigationItems = [
    {
      label: t('sidebar.dashboard'),
      path: '/dashboard',
      icon: 'LayoutDashboard',
      roles: ['system_admin', 'admin', 'it_staff', 'department_pic', 'pic'],
      category: 'Asset Management and Workflow'
    },
    {
      label: isPic ? t('sidebar.my_department_assets') : t('sidebar.asset_list'),
      path: '/asset-list',
      icon: 'Package',
      roles: ['system_admin', 'admin', 'it_staff', 'department_pic', 'pic'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.qr_scan'),
      path: '/scanner',
      icon: 'QrCode',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.add_asset'),
      path: '/asset-registration',
      icon: 'Plus',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: isPic ? t('sidebar.hardware_requests') : t('sidebar.assignment_management'),
      path: '/checkout-management',
      icon: 'UserCheck',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.department_audit'),
      path: '/department-audit',
      icon: 'ClipboardCheck',
      roles: ['department_pic', 'pic'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.lifecycle_planning'),
      path: '/lifecycle-planning',
      icon: 'TrendingUp',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.write_offs_disposals'),
      path: '/write-offs',
      icon: 'Trash2',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.software_licenses'),
      path: '/software',
      icon: 'Cloud',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Asset Management and Workflow'
    },
    {
      label: t('sidebar.supplier_management'),
      path: '/supplier-management',
      icon: 'Truck',
      roles: ['system_admin', 'admin', 'it_staff'],
      category: 'Organization'
    },
    {
      label: t('sidebar.user_registration'),
      path: '/admin/user-registration',
      icon: 'UserCog',
      roles: ['system_admin', 'admin'],
      category: 'Organization'
    },
    {
      label: isPic ? t('sidebar.my_team') : t('sidebar.employee_management'),
      path: '/admin/employee-management',
      icon: 'Users',
      roles: ['system_admin', 'admin', 'it_staff', 'manager', 'department_pic', 'pic'],
      category: 'Organization'
    },
    {
      label: t('sidebar.department_management'),
      path: '/admin/department-management',
      icon: 'Building2',
      roles: ['system_admin', 'admin', 'it_staff', 'manager'],
      category: 'Organization'
    },
    {
      label: t('sidebar.audit_management'),
      path: '/admin/audit-reports',
      icon: 'BarChart3',
      roles: ['system_admin', 'admin', 'it_staff', 'manager', 'pic', 'department_pic'],
      category: 'Organization'
    },
    {
      label: t('sidebar.location_management'),
      path: '/admin/location-management',
      icon: 'MapPin',
      roles: ['system_admin', 'admin', 'it_staff', 'manager'],
      category: 'Organization'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (onMobileClose) onMobileClose();
  };

  // Improved filtering: if user role is missing, default to showing nothing to be safe
  const filteredNavItems = navigationItems?.filter(item => {
    const userRole = user?.role || user?.app_metadata?.role;
    // Show item if user has one of the allowed roles
    return item.roles.includes(userRole);
  });

  const isActive = (path) => {
    const { pathname, search } = location;

    if (path === '/dashboard') {
      return pathname === '/' || pathname === '/dashboard';
    }

    if (path === '/asset-registration') {
      return pathname === '/asset-registration' && !search.includes('id=');
    }

    if (path === '/asset-list') {
      return pathname.startsWith('/asset-list') || (pathname === '/asset-registration' && search.includes('id='));
    }
    
    return pathname.startsWith(path);
  };

  const groupedNavItems = filteredNavItems.reduce((acc, item) => {
    const category = item.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[100] lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-card border-r border-border z-[110]
        transition-all duration-300 ease-in-out shadow-sm
        ${isCollapsed ? 'w-16' : 'w-60'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="h-16 flex items-center justify-center border-b border-border">
            {isCollapsed ? (
              <Panasonic className="h-11 w-11 text-[#0057B8]" />
            ) : (
              <div className="h-12 w-44 overflow-hidden flex items-center justify-center">
                <Panasonic className="h-8 w-auto scale-[2.1] origin-center text-[#0057B8]" />
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 no-scrollbar">
            {Object.entries(groupedNavItems).map(([category, items], index) => (
              <div 
                key={category} 
                className={`space-y-1 ${isCollapsed ? 'px-2' : 'px-4'} ${index > 0 ? 'mt-8' : ''}`}
              >
                {!isCollapsed && (
                  <h3 className="px-3 py-2 text-[10px] font-bold text-muted-foreground/70 uppercase tracking-[0.1em]">
                    {category === 'Asset Management and Workflow' ? t('sidebar.asset_management_workflow') : t('sidebar.organization')}
                  </h3>
                )}
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium
                        transition-all duration-200 group
                        ${isActive(item.path)
                          ? 'bg-primary/10 text-[#0057B8]'
                          : 'text-muted-foreground hover:text-gray-900 hover:bg-muted'
                        }
                        ${isCollapsed ? 'justify-center' : ''}
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon 
                        name={item.icon} 
                        size={18} 
                        className={`flex-shrink-0 transition-colors ${
                          isActive(item.path) ? 'text-[#0057B8]' : 'text-muted-foreground/70 group-hover:text-gray-600'
                        }`}
                      />
                      {!isCollapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* User Section */}
          <div className={`border-t border-border p-4 bg-muted/50 ${isCollapsed ? 'px-2' : ''}`}>
            {isCollapsed ? (
              <div className="flex justify-center">
                <div className="w-9 h-9 bg-card border border-border text-[#0057B8] rounded-xl flex items-center justify-center text-sm font-bold shadow-sm overflow-hidden">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    user?.full_name?.charAt(0) || 'U'
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-card border border-border text-[#0057B8] rounded-xl flex items-center justify-center text-sm font-bold shadow-sm overflow-hidden">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    user?.full_name?.charAt(0) || 'U'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-foreground truncate">
                    {user?.full_name || 'User'}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-medium truncate">
                    {user?.role === 'system_admin' ? t('roles.system_admin') : user?.role || t('roles.it_staff')}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Collapse Toggle (Desktop Only) */}
          <div className={`hidden lg:block border-t border-border p-2`}>
            <button
              onClick={onToggleCollapse}
              className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground/70 hover:text-gray-600 hover:bg-muted transition-colors"
            >
              <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={18} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;